import { Popper, TextField } from '@material-ui/core';
import Select from 'react-select';
import { Autocomplete } from '@material-ui/lab';
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import traduzirProdutoAtividade from 'utils/traduzirProdutoAtividade';
import OrcamentoMaterial from 'models/OrcamentoMaterial';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

type ModalFiltroProps = {
  modalFiltros: boolean;
  setModalFiltros: React.Dispatch<React.SetStateAction<boolean>>;
  orcamentoMateriais: OrcamentoMaterial[];
  setFiltroOrcamentoMateriais: React.Dispatch<
    React.SetStateAction<OrcamentoMaterial[]>
  >;
  setHasDadosFiltro: React.Dispatch<React.SetStateAction<boolean>>;
  limparFiltros: boolean;
  setQntFiltrosUsados: React.Dispatch<React.SetStateAction<string[]>>;
};

const styles = () => ({
  popper: {
    width: 'fit-content',
  },
});

const PopperMy = (props: any) => {
  return <Popper {...props} style={styles().popper} placement="bottom-start" />;
};

const ModalFiltro: React.FC<ModalFiltroProps> = ({
  modalFiltros,
  setModalFiltros,
  orcamentoMateriais,
  setFiltroOrcamentoMateriais,
  setHasDadosFiltro,
  limparFiltros,
  setQntFiltrosUsados,
}) => {
  const [campoBusca, setCampoBusca] = useState('');
  const [sequenciaFiltro, setSequenciaFiltro] = useState<number | string>();
  const [atividadeFiltro, setAtividadeFiltro] = useState<number>();

  const isFirstRender = useRef(true);

  const atividadesUsadas = useMemo(() => {
    return Array.from(
      orcamentoMateriais
        .filter(item => item.produtoAtividade)
        .reduce((map, item) => {
          const atividade = item.produtoAtividade;
          if (!map.has(atividade?.id)) {
            map.set(atividade?.id, atividade);
          }
          return map;
        }, new Map())
        .values(),
    );
  }, [orcamentoMateriais]);

  const opcoesDescricao = useMemo(() => {
    return orcamentoMateriais
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(t => t.material.descricao === item.material.descricao),
      )
      .map(item => ({
        label: item.material.descricao,
        value: String(item.id),
      }));
  }, [orcamentoMateriais]);

  const atividadeSelecionada = useMemo(() => {
    return atividadesUsadas?.find(a => a.id === atividadeFiltro) || null;
  }, [atividadesUsadas, atividadeFiltro]);

  function resetarValores() {
    setFiltroOrcamentoMateriais([]);
    setAtividadeFiltro(0);
    setCampoBusca('');
    setSequenciaFiltro('');
    setHasDadosFiltro(false);
    setQntFiltrosUsados([]);
    localStorage.removeItem('filtro_descricao');
    localStorage.removeItem('filtro_atividade');
    localStorage.removeItem('filtro_sequencia');
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    resetarValores();
  }, [limparFiltros]);

  function aplicarFiltros({
    descricao,
    atividade,
    sequencia: seq,
  }: {
    descricao: string | null | undefined;
    atividade: number | null | undefined;
    sequencia: number | string | null | undefined;
  }) {
    const quantidadeFiltrosUsados = [];
    // Salva o filtro no storage independentemente de encontrar resultados
    if (descricao) {
      localStorage.setItem('filtro_descricao', descricao);
      quantidadeFiltrosUsados.push('Descrição');
    } else {
      localStorage.setItem('filtro_descricao', '');
    }

    if (atividade !== null && atividade !== undefined && atividade !== 0) {
      localStorage.setItem('filtro_atividade', atividade.toString());
      quantidadeFiltrosUsados.push('Atividade');
    } else {
      localStorage.setItem('filtro_atividade', '');
    }

    if (seq) {
      localStorage.setItem('filtro_sequencia', seq.toString());
      quantidadeFiltrosUsados.push('Sequência');
    } else {
      localStorage.setItem('filtro_sequencia', '');
    }

    setQntFiltrosUsados(quantidadeFiltrosUsados.sort());

    // Se seq foi informado e não existe no array, retorna vazio
    if (seq && !orcamentoMateriais.some(m => m.sequenciaAtividade === seq)) {
      return [];
    }

    return orcamentoMateriais.filter(mat => {
      const matchDescricao = descricao
        ? mat.material.descricao.toLowerCase().includes(descricao.toLowerCase())
        : true;

      const matchAtividade = atividade
        ? mat.produtoAtividade?.id === atividade
        : true;

      const matchSequencia = seq ? mat.sequenciaAtividade === seq : true;

      return matchDescricao && matchAtividade && matchSequencia;
    });
  }

  async function handleFiltro() {
    const resultado = aplicarFiltros({
      descricao: campoBusca,
      atividade: atividadeFiltro,
      sequencia: sequenciaFiltro,
    });

    setFiltroOrcamentoMateriais(resultado);
  }

  async function checkSavedParams() {
    const filtroDesc = localStorage.getItem('filtro_descricao');
    const filtroAtiv = localStorage.getItem('filtro_atividade');
    const filtroSeq = localStorage.getItem('filtro_sequencia');

    if (filtroAtiv) setAtividadeFiltro(Number(filtroAtiv));
    if (filtroSeq) setSequenciaFiltro(Number(filtroSeq));
    if (filtroDesc) setCampoBusca(String(filtroDesc ?? ''));

    const resultado = aplicarFiltros({
      descricao: filtroDesc || null,
      atividade: Number(filtroAtiv) || null,
      sequencia: Number(filtroSeq) || null,
    });
    setFiltroOrcamentoMateriais(resultado);
  }

  useEffect(() => {
    checkSavedParams();
  }, [orcamentoMateriais]);

  useEffect(() => {
    setHasDadosFiltro(!!(atividadeFiltro || sequenciaFiltro || campoBusca));
  }, [atividadeFiltro, sequenciaFiltro, campoBusca, setHasDadosFiltro]);

  return (
    <Modal
      isOpen={modalFiltros}
      toggle={() => setModalFiltros(false)}
      size="lg"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={() => setModalFiltros(false)}>
        Filtros da lista de materiais
      </ModalHeader>

      <ModalBody
        style={{ maxHeight: '65vh', overflowY: 'auto', padding: '1.5rem' }}
      >
        <Row className="mb-3">
          <Col sm="12">
            <Select
              className="w-100"
              classNamePrefix="react-select"
              closeMenuOnSelect={false}
              placeholder="Descrição do material..."
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              isSearchable
              isClearable
              options={opcoesDescricao}
              onChange={option => {
                setCampoBusca(option ? option.label : '');
              }}
              value={
                campoBusca ? { label: campoBusca, value: campoBusca } : null
              }
            />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col sm="9">
            <Autocomplete
              style={{ display: 'flex', alignItems: 'center' }}
              options={atividadesUsadas || []}
              PopperComponent={PopperMy}
              getOptionLabel={option => traduzirProdutoAtividade(option)}
              value={atividadeSelecionada}
              onChange={(event, newValue) => {
                setAtividadeFiltro(newValue ? newValue.id : 0);
              }}
              renderOption={option => (
                <>
                  {traduzirProdutoAtividade(option)}
                  {option.codSGE && ` (Ref. DomSGE: ${option.codSGE})`}
                </>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Atividade"
                  className="m-0"
                  margin="dense"
                  variant="outlined"
                />
              )}
            />
          </Col>

          <Col sm="3">
            <TextField
              fullWidth
              label="Sequência"
              variant="outlined"
              id="outlined-basic"
              inputProps={{ min: 0 }}
              size="small"
              value={sequenciaFiltro ?? ''}
              type="number"
              // maxLength={2}
              onChange={e => {
                const value = Number(e.target.value);

                if (value >= 0 || e.target.value === '') {
                  setSequenciaFiltro(value);
                }
              }}
            />
          </Col>
        </Row>

        <Row>
          <Col sm="6">
            <Button
              className="w-100"
              style={{ height: 44, fontWeight: 600 }}
              color="primary"
              type="button"
              onClick={handleFiltro}
            >
              Filtrar
            </Button>
          </Col>

          <Col sm="6">
            <Button
              className="w-100"
              style={{ height: 44, fontWeight: 600 }}
              color="danger"
              type="button"
              onClick={resetarValores}
            >
              Limpar
            </Button>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default memo(ModalFiltro);
